import React, { useContext, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Typography } from 'antd';
import { formatISO } from 'date-fns/formatISO';

import { useDeletePartnerClosureRequest } from '../../api/deletePartnerClosureRequest';
import { ClosureRequest } from '../../types';
import ScopedCta from '@/components/ScopedCta';
import { scopes } from '@/config';
import { PartnerData } from '@/features/lp-village/partners/api/useLPPartner';
import useArchivePartner from '@/features/lp-village/partners/hooks/useArchivePartner';
import { CurrentPartnerContext } from '@/features/lp-village/partners/views/LPPartnerDetails';
import useError from '@/hooks/useError';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

const { Text } = Typography;

export const RestoreThisOrganisation = ({
  closureRequest,
}: {
  closureRequest: ClosureRequest;
}) => {
  const addError = useError();
  const addSuccess = useToasts((state) => state.addItem);

  const { partnerState, setPartner } = useContext<PartnerData>(
    CurrentPartnerContext
  );
  const [, unarchivePartner] = useArchivePartner(partnerState.data.id, false);

  const [open, setOpen] = useState(false);

  // Delete closure request
  const { mutateAsync: cancelClosureRequest, isPending } =
    useDeletePartnerClosureRequest();

  const showModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOk = async () => {
    try {
      await cancelClosureRequest();

      if (partnerState.data.is_archived) {
        const { success } = await unarchivePartner(partnerState.data);

        if (success) {
          setPartner({
            ...partnerState.data,
            is_archived: false,
          });
        } else {
          addError(
            'Organisation restored but could not be unarchived automatically. Please unarchive it manually.'
          );
        }
      }

      addSuccess(
        {
          msg: 'Successfully restored the organisation. No anonymization will be applied.',
          type: MessageType.Success,
        },
        'root'
      );
    } catch (error) {
      console.error('Error restoring closure request:', error);

      addError('Failed to restore the organisation. Please try again later.');
    }
  };

  let date = 'N/A';
  if (closureRequest.approved_at) {
    date = formatISO(new Date(closureRequest.approved_at!), {
      representation: 'date',
    });
  }

  return (
    <>
      <ScopedCta
        component={Button}
        type="primary"
        danger
        disabled={isPending}
        onClick={showModal}
        data-testid="restore-partner"
        requiredScopes={[scopes.MANAGE_LPVILLAGE_PARTNER]}
      >
        Restore this organisation
      </ScopedCta>
      <Modal
        destroyOnClose
        open={open}
        onOk={handleOk}
        confirmLoading={isPending}
        okText="Restore this organisation"
        onCancel={handleCancel}
        okButtonProps={{
          'data-testid': 'ok-button',
          danger: true,
          variant: 'outlined',
          disabled: isPending,
        }}
        width={{
          xs: '90%',
          sm: '80%',
          md: '70%',
          lg: '60%',
          xl: '41.25rem',
          xxl: '41.25rem',
        }}
        styles={{
          body: {
            paddingBottom: '2rem',
          },
          footer: {
            marginTop: '2rem',
          },
        }}
      >
        <Flex
          gap="1.5rem"
          vertical
          style={{
            background: 'transparent',
          }}
        >
          {/* Icon + confirm phrase */}
          <Flex gap="0.75rem" vertical align="center">
            <ExclamationCircleOutlined
              style={{
                fontSize: '3.625rem',
                color: '#C82F21',
                padding: '.4375rem',
              }}
            />
          </Flex>
          {/* Partnership Name + Content + Reason + FAQ */}
          <Text
            style={{
              fontSize: '1.25rem',
              textAlign: 'center',
            }}
          >
            This organization was closed on {date}. Restoring it will reactivate
            access and cancel the scheduled anonymization process.
          </Text>
        </Flex>
      </Modal>
    </>
  );
};
